import api from '../clients/api';
import { addInlineTableStyles } from '../components/utils/addInlineTableStyles';
import { createPrintHTMLDoc } from '../components/utils/createPrintHTMLDoc';
import services from '../services';
import { notifyBugsnagHandledError } from './bugsnagClient';

/**
 * Converts HTML to PDF blob using the document engine
 */
const getPdfBlob = async (styledHtml: string, accessToken?: string): Promise<Blob> => {
	return await services.pdf.convertHtmlToPdfBlob(
		styledHtml,
		{
			renderMethod: 'document-engine',
		},
		accessToken
	);
};

/**
 * Triggers a browser download for the given Blob
 * @param blob - The file content as a Blob
 * @param filename - The name of the downloaded file (including extension)
 */
export const triggerBlobDownload = (blob: Blob, filename: string): void => {
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

/**
 * Downloads HTML content as a PDF file
 * @param html - The HTML content to convert and download
 * @param filename - The name of the file (without extension)
 * @param accessToken - The public access token for the document
 */
export const downloadHtmlAsPdf = async (
	html: string,
	filename: string,
	accessToken?: string
): Promise<void> => {
	try {
		const styledHtml = addInlineTableStyles(html);
		const blob = await getPdfBlob(styledHtml, accessToken);
		triggerBlobDownload(blob, `${filename}.pdf`);
	} catch (error) {
		notifyBugsnagHandledError(error as Error);
		throw error;
	}
};

/**
 * Downloads HTML content as a .docx file
 * @param html - The HTML content to convert and download
 * @param filename - The name of the file (without extension)
 */
export const downloadHtmlAsDocx = async (html: string, filename: string): Promise<void> => {
	try {
		const styledHtml = addInlineTableStyles(html);
		const blob = await api.document.convertHtmlToDocx(styledHtml);
		triggerBlobDownload(blob, `${filename}.docx`);
	} catch (error) {
		notifyBugsnagHandledError(error as Error);
		throw error;
	}
};

/**
 * Opens a print dialog with HTML content
 * @param title - The title of the document
 * @param html - The HTML content to print
 */
export const printHtmlDocument = (title: string, html: string): void => {
	try {
		const styledHtml = addInlineTableStyles(html);
		const printDocument = createPrintHTMLDoc(title, styledHtml);

		// Open HTML in a new window and print
		const printWindow = window.open('', '_blank');
		if (printWindow) {
			printWindow.document.write(printDocument);
			printWindow.document.close();

			// Wait for content to load, then trigger print
			printWindow.onload = () => {
				setTimeout(() => {
					printWindow.print();
				}, 250);
			};
		}
	} catch (error) {
		notifyBugsnagHandledError(error as Error);
		throw error;
	}
};
